<template>
  <div class="editor-container">
    <!-- Intro Section -->
    <div class="intro-section">
      <div class="intro-icon">
        <svg preserveAspectRatio="none" width="100%" height="100%" overflow="visible" style="display: block;" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.125 9.16667C12.75 9.16667 13.3 8.825 13.5833 8.30833L16.5667 2.9C16.875 2.35 16.475 1.66667 15.8417 1.66667H3.50833L2.725 0H0V1.66667H1.66667L4.66667 7.99167L3.54167 10.025C2.93333 11.1417 3.73333 12.5 5 12.5H15V10.8333H5L5.91667 9.16667H12.125ZM4.3 3.33333H14.425L12.125 7.5H6.275L4.3 3.33333ZM5 13.3333C4.08333 13.3333 3.34167 14.0833 3.34167 15C3.34167 15.9167 4.08333 16.6667 5 16.6667C5.91667 16.6667 6.66667 15.9167 6.66667 15C6.66667 14.0833 5.91667 13.3333 5 13.3333ZM13.3333 13.3333C12.4167 13.3333 11.675 14.0833 11.675 15C11.675 15.9167 12.4167 16.6667 13.3333 16.6667C14.25 16.6667 15 15.9167 15 15C15 14.0833 14.25 13.3333 13.3333 13.3333Z" :fill="'#5587BC'"/>
        </svg>
      </div>
      <p class="intro-text">
        <span class="intro-bold">Place Order</span> will create an order for the selected patient when the Trigger event happened.
      </p>
    </div>

    <!-- Properties Section -->
    <div class="properties-section">
      <!-- Divider -->
      <div class="section-divider">
        <span class="divider-text">Select Products</span>
      </div>

      <!-- No Items State -->
      <div v-if="displayItems.length === 0" class="empty-state">
        <InlineNotification
          theme="warning"
          message="No item has been added to order."
          :with_close_btn="false"
        />
        <ResponsiveButton
          :display_name="'Open Product Menu'"
          :button_type="'filled'"
          :built_in_theme="'dark'"
          size="small"
          width_type="fill-whole"
          @click-button="openProductMenu"
        >
          <template #prefix>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6H12L10 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V8C22 6.9 21.1 6 20 6ZM20 18H4V8H20V18ZM8 13.01L10.5 15.51L14.01 12L18 16H6L8 13.01Z" fill="white"/>
            </svg>
          </template>
        </ResponsiveButton>
      </div>

      <!-- Items List State -->
      <div v-else class="items-section">
        <div class="items-header">
          <span class="header-label">Items:</span>
          <span class="header-label">Price</span>
        </div>
        <div class="items-list">
          <div
            v-for="(item, index) in displayItems"
            :key="index"
            class="item-row"
          >
            <div class="expand-btn-wrapper">
              <div class="expand-btn-container">
              <!-- Show expand button only if item has subtests -->
              <button 
                v-if="item.subtests && item.subtests.length > 0"
                class="expand-btn" 
                @click="toggleExpand(index)"
              >
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 12 12" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                :style="{ transform: expandedItems[index] ? 'rotate(180deg)' : 'rotate(0deg)' }"
              >
                <path d="M2 4L6 8L10 4" stroke="#333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              </button>
              <!-- Placeholder for items without subtests (fees) -->
              <div v-else class="expand-btn"></div>
              <div class="item-info">
                <span class="item-name">{{ item.name }}</span>
              </div>
              </div> 
              <span class="item-price">$ {{ item.price }}</span>
            </div>
            <div v-if="expandedItems[index]" class="item-subtests">
                <div
                  v-for="(subtest, subIndex) in item.subtests"
                  :key="subIndex"
                  class="subtest-item"
                >
                  • {{ subtest }}
                </div>
              </div>
          </div>
        </div>
        <div class="cart-subtotal">
          <span class="subtotal-label">Cart Subtotal:</span>
          <span class="subtotal-value">$ {{ cartSubtotal }}</span>
        </div>
        <button class="upload-documents-btn" @click="openProductMenu">
          <img src="@/assets/MyWorkBench/Workflow/card_action_edit.svg">
          Edit Order
        </button>
      </div>

      <!-- Save Configuration Button -->
      <SaveConfigure
        :disabled="!isSaveActive"
        @save-configuration="handleSave"
      />
    </div>

    <!-- Order Dialog (reuses real PlaceOrderDialog with configMode) -->
    <NewOrderDialog
      :visible="showConfigDialog"
      :config-mode="true"
      :initial-items="[]"
      :initial-is-dbs-version="localData.isDbsVersion"
      :initial-order-items="savedOrderItems"
      @close="showConfigDialog = false"
      @save="handleOrderSave"
    />
  </div>
</template>

<script>
import SaveConfigure from '@/components/workflow/SaveConfigure.vue';
import NewOrderDialog from '@/views/MyPatient-demo/NewOrderDialog.vue';

export default {
  name: 'ActionPlaceOrderEditor',
  components: {
    SaveConfigure,
    NewOrderDialog
  },
  props: {
    data: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['save', 'close'],
  data() {
    return {
      localData: {
        items: this.data.items || [],
        isDbsVersion: this.data.isDbsVersion ?? null,
        orderPayload: this.data.payload || this.data.orderPayload || null,
        orderItems: this.data.orderItems || [],
      },
      expandedItems: {},
      showConfigDialog: false
    };
  },
  computed: {
    clinicId() {
      return this.$store.state.login?.userInfo?.clinic_id || null;
    },
    cartSubtotal() {
      const payload = this.localData.orderPayload || this.data.payload;
      if (payload) {
        // Calculate from line_items and fees (prices are already in cents)
        const lineItemsTotal = payload.line_items?.reduce((sum, item) => {
          return sum + (item.total || 0);
        }, 0) || 0;
        const feesTotal = payload.fees?.reduce((sum, fee) => {
          return sum + (fee.amount || 0);
        }, 0) || 0;
        return (lineItemsTotal + feesTotal).toFixed(2);
      }
      return this.localData.items.reduce((sum, item) => {
        return sum + parseFloat(item.price);
      }, 0).toFixed(2);
    },
    isSaveActive() {
      return this.localData.items.length > 0 || (this.localData.orderPayload?.line_items?.length > 0);
    },
    savedOrderItems() {
      return this.localData.orderItems || [];
    },
    displayItems() {
      // Check if we have orderPayload (could be from data.payload or localData.orderPayload)
      const payload = this.localData.orderPayload || this.data.payload;
      if (payload) {
        // Convert line_items to display format (prices are in cents)
        const items = payload.line_items?.map(item => ({
          name: item.item_name,
          price: (item.total || 0).toFixed(2),
          subtests: item.sub_items?.map(sub => sub.item_name) || []
        })) || [];
        
        // Add fees as items (fees are also in cents)
        const feeItems = payload.fees?.map(fee => ({
          name: fee.notes || fee.type,
          price: (fee.amount || 0).toFixed(2),
          subtests: []
        })) || [];
        
        return [...items, ...feeItems];
      }
      return this.localData.items;
    }
  },
  watch: {
    'data.items': {
      handler(newVal) {
        if (newVal && JSON.stringify(newVal) !== JSON.stringify(this.localData.items)) {
          this.localData.items = newVal || [];
        }
      }
    },
    'data.isDbsVersion': {
      handler(newVal) {
        this.localData.isDbsVersion = newVal ?? null;
      }
    },
    'data.orderPayload': {
      handler(newVal) {
        if (newVal && JSON.stringify(newVal) !== JSON.stringify(this.localData.orderPayload)) {
          this.localData.orderPayload = newVal;
        }
      },
      deep: true
    },
    'data.payload': {
      handler(newVal) {
        if (newVal && JSON.stringify(newVal) !== JSON.stringify(this.localData.orderPayload)) {
          this.localData.orderPayload = newVal;
        }
      },
      deep: true
    },
    'data.orderItems': {
      handler(newVal) {
        if (newVal && JSON.stringify(newVal) !== JSON.stringify(this.localData.orderItems)) {
          this.localData.orderItems = newVal;
        }
      },
      deep: true
    }
  },
  methods: {
    handleInput() {
      // Update the shared data instance directly
      this.data.items = this.localData.items;
    },
    toggleExpand(index) {
      this.expandedItems[index] = !this.expandedItems[index];
      this.$forceUpdate();
    },
    openProductMenu() {
      this.showConfigDialog = true;
    },
    handleOrderSave(data) {
      const items = data.items || data;
      const payload = data.payload || null;
      const isDbsVersion = data.isDbsVersion ?? null;
      const orderItems = data.orderItems || [];
      
      if (payload) {
        console.log('[ActionPlaceOrderEditor] order payload before sanitization:', JSON.parse(JSON.stringify(payload)));
        
        // Add clinic_id from store
        payload.clinic_id = this.clinicId;
        
        // Sanitize notification fields - always override with empty email/phone
        payload.notification = {
          send_email: true,
          send_text: true,
          send_date: "",
          email: "",
          phone: ""
        };
        
        console.log('[ActionPlaceOrderEditor] order payload after sanitization:', JSON.parse(JSON.stringify(payload)));
        
        this.localData.orderPayload = payload;
        this.data.orderPayload = payload;
      }
      
      console.log('[ActionPlaceOrderEditor] saved items:', JSON.parse(JSON.stringify(items)), 'isDbsVersion:', isDbsVersion, 'orderItems count:', orderItems.length);
      this.localData.items = items;
      this.localData.isDbsVersion = isDbsVersion;
      this.localData.orderItems = orderItems;
      this.data.items = items;
      this.data.isDbsVersion = isDbsVersion;
      this.data.orderItems = orderItems;
    },
    handleSave() {
      const isValid = this.localData.items.length > 0 || (this.localData.orderPayload?.line_items?.length > 0);

      // If we have orderPayload, wrap it in a payload object for backend
      if (this.localData.orderPayload) {
        const saveData = {
          payload: this.localData.orderPayload,
          orderItems: this.localData.orderItems,
          isDbsVersion: this.localData.isDbsVersion,
          configured: isValid
        };
        console.log('[ActionPlaceOrderEditor] Saving order with payload wrapper:', JSON.parse(JSON.stringify(saveData)));
        this.$emit('save', saveData);
      } else {
        // Legacy format fallback
        console.log('[ActionPlaceOrderEditor] Saving legacy format with items:', this.localData.items);
        this.$emit('save', {
          items: this.localData.items,
          isDbsVersion: this.localData.isDbsVersion,
          orderItems: this.localData.orderItems,
          configured: isValid
        });
      }
    }
  }
};
</script>

<style scoped>
.editor-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  height: 100%;
  background: #F9FBFD;
}

/* Intro Section */
.intro-section {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  background: #E4F3F5;
  border-radius: 5px;
}

.intro-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.intro-text {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #5587BC;
  margin: 0;
}

.intro-bold {
  font-weight: 700;
}

/* Properties Section */
.properties-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  overflow-y: auto;
  flex: 1;
}

/* Section Divider */
.section-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.section-divider::before,
.section-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #C4C4C4;
}

.divider-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #666;
  white-space: nowrap;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

/* Items Section */
.items-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.items-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;
}

.header-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #333;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.item-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 0px 4px;
  border-radius: 4px;
}

.expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: transform 0.2s;
}

.item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-name {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #333;
}

.item-subtests {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
  padding-left: 18px;
}

.subtest-item {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 16px;
  color: #666;
}

.item-price {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #333;
  flex-shrink: 0;
}

.cart-subtotal {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 8px;
  border-top: 1px dashed #C4C4C4;
}

.subtotal-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #333;
}

.subtotal-value {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #004879;
}

.upload-documents-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 32px;
  padding: 10px 24px;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #004879;
  background: #FFFFFF;
  border: 1px solid #004879;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-documents-btn:hover {
  background: #F0F7FF;
}
.expand-btn-wrapper{
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.expand-btn-container{
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
